import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShieldCheck, LogOut, Search, Smartphone, MailQuestion, Inbox, Clock, MapPin, Globe, X,
} from "lucide-react";

const statusColors = {
  new: "bg-blue-100 text-blue-700",
  reviewed: "bg-green-100 text-green-700",
  archived: "bg-slate-100 text-slate-500",
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${statusColors[status] || statusColors.new}`}>
    {status}
  </span>
);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("registrations");
  const [stats, setStats] = useState(null);
  const [regs, setRegs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  const loadStats = useCallback(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const loadRegs = useCallback(() => {
    api.get("/registrations", { params: { q: q || undefined, status: statusFilter } })
      .then((r) => setRegs(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [q, statusFilter]);

  const loadContacts = useCallback(() => {
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadStats(); loadContacts(); }, [loadStats, loadContacts]);
  useEffect(() => {
    const t = setTimeout(loadRegs, 250);
    return () => clearTimeout(t);
  }, [loadRegs]);

  const changeStatus = async (id, status, type = "reg") => {
    try {
      const path = type === "reg" ? `/registrations/${id}/status` : `/contacts/${id}/status`;
      await api.patch(path, { status });
      toast.success("Status updated.");
      if (type === "reg") { loadRegs(); loadStats(); setSelected((s) => s && s.id === id ? { ...s, status } : s); }
      else { loadContacts(); setSelectedContact((s) => s && s.id === id ? { ...s, status } : s); }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doLogout = () => { logout(); navigate("/login"); };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-900 text-white">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <span className="font-display text-lg font-bold tracking-tight text-slate-900">DeviceLox</span>
            <Badge variant="secondary" className="ml-1">Owner</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-500 sm:inline">{user?.email}</span>
            <Button variant="outline" size="sm" className="rounded-md" onClick={doLogout} data-testid="logout-button">
              <LogOut className="mr-1.5 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 py-8">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        <p className="mt-1 text-slate-500">Review device registrations and contact inquiries.</p>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Smartphone} label="Total Registrations" value={stats?.total_registrations} testid="stat-total" />
          <StatCard icon={Inbox} label="New Registrations" value={stats?.new_registrations} testid="stat-new" />
          <StatCard icon={MailQuestion} label="Contact Messages" value={stats?.total_contacts} testid="stat-contacts" />
          <StatCard icon={Clock} label="New Messages" value={stats?.new_contacts} testid="stat-new-contacts" />
        </div>

        <div className="mt-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="dashboard-tabs">
              <TabsTrigger value="registrations" data-testid="tab-registrations">Registrations</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts">Contact Messages</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {tab === "registrations" && (
          <div className="mt-4 rounded-md border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone…" className="pl-9" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-44" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {regs.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="py-10 text-center text-slate-400">No registrations found.</TableCell></TableRow>
                  ) : regs.map((r) => (
                    <TableRow key={r.id} className="cursor-pointer" onClick={() => setSelected(r)} data-testid={`reg-row-${r.id}`}>
                      <TableCell className="font-medium text-slate-900">{r.first_name} {r.last_name}</TableCell>
                      <TableCell className="text-sm text-slate-600"><div>{r.email}</div><div className="text-slate-400">{r.phone}</div></TableCell>
                      <TableCell className="text-sm text-slate-600">{r.brand} {r.model}<div className="text-slate-400">{r.device_type}</div></TableCell>
                      <TableCell className="text-sm text-slate-500">{fmt(r.created_at)}</TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div className="mt-4 rounded-md border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="py-10 text-center text-slate-400">No messages yet.</TableCell></TableRow>
                  ) : contacts.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer" onClick={() => setSelectedContact(c)} data-testid={`contact-row-${c.id}`}>
                      <TableCell className="font-medium text-slate-900">{c.name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{c.email}</TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-slate-600">{c.message}</TableCell>
                      <TableCell className="text-sm text-slate-500">{fmt(c.created_at)}</TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Registration detail */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="reg-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{selected.first_name} {selected.last_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Detail label="Email" value={selected.email} />
                  <Detail label="Phone" value={selected.phone} />
                  <Detail label="ZIP" value={selected.zip} />
                  <Detail label="Device type" value={selected.device_type} />
                  <Detail label="Brand" value={selected.brand} />
                  <Detail label="Model" value={selected.model} />
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Consent Proof</p>
                  <div className="mt-3 space-y-2 text-sm text-slate-600">
                    <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> {fmt(selected.consent_timestamp)}</p>
                    <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> IP: {selected.ip_address}</p>
                    <p className="flex items-start gap-2 break-all"><Globe className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /> {selected.source_url || "—"}</p>
                    <p className="mt-2 rounded bg-white p-3 text-xs leading-relaxed text-slate-500">{selected.consent_text}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Status:</span>
                  <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v, "reg")}>
                    <SelectTrigger className="w-40" data-testid="reg-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact detail */}
      <Dialog open={!!selectedContact} onOpenChange={(o) => !o && setSelectedContact(null)}>
        <DialogContent className="max-w-lg" data-testid="contact-detail-dialog">
          {selectedContact && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{selectedContact.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <Detail label="Email" value={selectedContact.email} />
                <Detail label="Phone" value={selectedContact.phone || "—"} />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Message</p>
                  <p className="mt-2 whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 p-4 text-slate-600">{selectedContact.message}</p>
                </div>
                <p className="text-slate-500">Received: {fmt(selectedContact.created_at)}</p>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Status:</span>
                  <Select value={selectedContact.status} onValueChange={(v) => changeStatus(selectedContact.id, v, "contact")}>
                    <SelectTrigger className="w-40" data-testid="contact-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{label}</span>
      <Icon className="h-4 w-4 text-slate-300" />
    </div>
    <p className="mt-2 font-display text-3xl font-bold text-slate-900">{value ?? "—"}</p>
  </div>
);

const Detail = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{label}</p>
    <p className="mt-0.5 text-slate-800">{value}</p>
  </div>
);
